<template>
  <article class="package-tile" :class="{ 'package-tile--active': isActive }">
    <div class="package-tile__top">
      <span class="package-badge">{{ badgeText }}</span>
      <h3 class="package-tile__title">{{ title }}</h3>
    </div>
    <div class="package-tile__price">
      <PriceAmount :cents="pkg.price_cents" monthly block />
    </div>
    <div class="package-tile__meta">
      <span>{{ quantityText }}</span>
      <span v-if="isFree">keine zusätzlichen Einträge</span>
      <span v-if="showExtra">
        <PriceAmount
          :cents="extraQuantityPriceCents"
          suffix="je zusätzlichem Eintrag"
          inline
        />
      </span>
      <span v-if="durationText">{{ durationText }}</span>
    </div>
    <div class="package-tile__advantages">
      <PackageAdvantages :base-type="pkg.base_type" :source-kind="pkg.source_kind" />
    </div>

    <router-link
      v-if="cta.kind === 'enabled'"
      :to="cta.route"
      class="btn package-tile__cta"
      :class="isFree ? 'btn-secondary' : 'btn-success'"
    >
      {{ cta.label }}
    </router-link>

    <span v-else-if="cta.kind === 'active'" class="package-tile__cta-wrap">
      <span class="btn btn-outline-secondary package-tile__cta disabled" aria-disabled="true">
        {{ cta.label }}
      </span>
      <button
        type="button"
        class="package-tile__info-btn"
        data-bs-toggle="popover"
        data-bs-trigger="focus"
        data-bs-placement="top"
        data-bs-content="Dieses Paket ist aktuell aktiv."
        aria-label="Information zum aktiven Paket"
      ><Icon icon="fa-pro-solid:circle-info" /></button>
    </span>

    <span v-else-if="cta.kind === 'downgrade'" class="package-tile__cta-wrap">
      <span class="btn btn-outline-secondary package-tile__cta disabled" aria-disabled="true">
        {{ cta.label }}
      </span>
      <button
        type="button"
        class="package-tile__info-btn"
        data-bs-toggle="popover"
        data-bs-trigger="focus"
        data-bs-placement="top"
        data-bs-content="Das Zurückstufen eines Paketes ist derzeit leider nicht möglich, bitte wenden Sie sich an den Support."
        aria-label="Information zum Zurückstufen"
      ><Icon icon="fa-pro-solid:circle-info" /></button>
    </span>

    <span v-else class="btn btn-outline-secondary package-tile__cta disabled" aria-disabled="true">
      {{ cta.label }}
    </span>
  </article>
</template>

<script>
import PriceAmount from '@/components/general/PriceAmount.vue'
import PackageAdvantages from '@/components/packages/PackageAdvantages.vue'
import {
  getDurationText,
  getExtraQuantityPriceCents,
  getPackageBadge,
  getTileCtaState,
  getTileQuantity,
  getTileTitle,
  isActivePackage,
  isFreePackage,
  showExtraQuantityPrice,
} from '@/utils/packagePolicy'

export default {
  name: 'PackageTile',
  components: {PackageAdvantages, PriceAmount},
  props: {
    row: {type: Object, required: true},
    pkg: {type: Object, required: true},
    index: {type: Number, required: true},
    routeQuery: {type: Object, default: () => ({})},
  },
  computed: {
    isActive() {
      return isActivePackage(this.row, this.pkg)
    },
    isFree() {
      return isFreePackage(this.pkg)
    },
    badgeText() {
      if (this.isFree) return 'eingeschränktes Paket'
      if (this.isActive) return 'Aktiv'
      return getPackageBadge(this.pkg, this.index)
    },
    title() {
      return getTileTitle(this.pkg)
    },
    extraQuantityPriceCents() {
      return getExtraQuantityPriceCents(this.pkg)
    },
    quantityText() {
      return getTileQuantity(this.pkg)
    },
    showExtra() {
      return showExtraQuantityPrice(this.pkg)
    },
    durationText() {
      return getDurationText(this.pkg)
    },
    cta() {
      return getTileCtaState(this.row, this.pkg, this.routeQuery)
    },
  },
}
</script>

<style scoped lang="scss">
.package-tile {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-height: 100%;
  padding: 1rem 1.05rem 1.05rem;
  border: 1px solid var(--base-type-border, #d8e1ea);
  background: #ffffff;
  box-shadow: 0 4px 14px rgba(10, 33, 48, 0.04);
  transition:
    border-color 0.15s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: color-mix(in srgb, var(--base-type-primary, #d8e1ea) 50%, #d8e1ea);
    box-shadow: 0 10px 22px rgba(10, 33, 48, 0.08);
    transform: translateY(-1px);
  }
}

.package-tile--active {
  border-color: color-mix(in srgb, var(--base-type-primary, #8dc29a) 65%, #8dc29a);
  background: color-mix(in srgb, var(--base-type-soft, #f5fbf6) 50%, #ffffff);
  box-shadow: 0 8px 20px rgba(10, 33, 48, 0.06);

  &:hover {
    transform: none;
  }
}

.package-tile__top {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.package-tile__title {
  margin: 0;
  font-size: 1rem;
  color: #10273d;
}

.package-tile__price :deep(.price-amount__value) {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #0f3553;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.85);
}

.package-tile--active .package-tile__price :deep(.price-amount__value) {
  color: #0f5a2d;
}

.package-tile__meta {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: #4e6477;
  font-size: 0.88rem;
}

.package-tile__advantages {
  flex: 1 1 auto;
  padding-top: 0.55rem;
  border-top: 1px solid color-mix(in srgb, var(--base-type-border, #e4ebf1) 75%, #e4ebf1);
}

.package-tile__cta {
  margin-top: auto;
}

.package-tile__cta-wrap {
  margin-top: auto;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
}

.package-tile__cta-wrap .package-tile__cta {
  flex: 1;
}

.package-tile__info-btn {
  min-height: calc(1.5em + 0.75rem + 2px);
  padding: .375rem .35rem;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: #476582;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.package-badge {
  display: inline-flex;
  width: fit-content;
  margin-bottom: 0.15rem;
  padding: 0.28rem 0.55rem;
  border: 1px solid var(--base-type-border);
  background: var(--base-type-soft);
  color: var(--base-type-text);
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.package-tile--active .package-badge {
  border-color: #8dc29a;
  background: #e8f6ea;
  color: var(--base-type-text);
}
</style>
